import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = window.atob(base64);
  const arr     = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Manages Web Push notification permission and browser subscription.
 *
 * Returns:
 *   permission     — 'default' | 'granted' | 'denied'
 *   isSubscribed   — true when an active subscription exists in the backend
 *   isLoading      — true while subscribing/unsubscribing
 *   error          — error message string or null
 *   subscribe()    — request permission → register SW → subscribe → POST backend
 *   unsubscribe()  — unsubscribe from browser + DELETE backend record
 *   sendTest()     — POST /api/push/test to verify delivery
 */
export function usePushNotifications() {
  const [permission,   setPermission]   = useState(() =>
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading,    setIsLoading]    = useState(false);
  const [error,        setError]        = useState(null);

  // On mount, check if there is already an active subscription
  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    navigator.serviceWorker.getRegistration('/sw.js').then(async (reg) => {
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Push notifications are not supported in this browser.');
      }

      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Notification permission denied.');

      // Get VAPID public key from server
      const { data: { vapid_public_key } } = await axios.get(`${API_BASE_URL}/api/push/vapid-key`);

      // Register (or reuse) service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Subscribe in the browser
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid_public_key),
      });

      const keys = subscription.toJSON().keys || {};
      await axios.post(`${API_BASE_URL}/api/push/subscribe`, {
        endpoint:   subscription.endpoint,
        p256dh:     keys.p256dh,
        auth:       keys.auth,
        user_agent: navigator.userAgent,
      });

      setIsSubscribed(true);
    } catch (err) {
      setError(err.message || 'Failed to enable notifications.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await axios.delete(`${API_BASE_URL}/api/push/unsubscribe`, {
            data: { endpoint: sub.endpoint },
          });
          await sub.unsubscribe();
        }
      }
      setIsSubscribed(false);
    } catch (err) {
      setError(err.message || 'Failed to disable notifications.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/push/test`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Test push failed.');
    }
  }, []);

  return { permission, isSubscribed, isLoading, error, subscribe, unsubscribe, sendTest };
}
